import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatCurrency, formatDay } from '~/lib/format';
import { candles } from '~data';
import { valueSeries, type Basis } from '~/stores/series-store';
import { clearHover, hoverAt, setHoverAt } from '~/stores/crosshair-store';

// One unit of a fund, on all three bases at once.
//
// The same chart the site shows on a fund page, and the one thing neither the
// issuer's own factsheet nor a price site will draw: a covered-call ETF's unit
// price can be down double digits while a holder is comfortably ahead once the
// distributions are counted. Showing only the price — which is what this panel
// did — tells the most pessimistic third of the story.
//
// One shared y-axis, in dollars, never indexed and never a second scale. That is
// what makes it readable: all three lines start from the same close, so the gap
// between them *is* the distributions, and the gap between the top two *is* the
// compounding. Rescaling any of them would destroy exactly that.
//
// Monochrome, and separated by line texture rather than by hue. These are not
// three things, they are one holding under three assumptions, and colour would
// imply otherwise; the series palette is also spoken for by Compare, where hue
// means *which fund*. It matches the site for the same reasons.
//
// Three genuinely distinct textures rather than the site's two solids and a
// dash, because two of these lines cross far more than they look like they
// should. Cash is above price at every bar of every fund — banked cash is never
// negative, so that ordering is structural. Reinvested against cash is not:
// reinvesting into a price that then falls leaves a holder behind having simply
// banked it, and that is not an edge case. EMAX spends 44% of its bars with the
// reinvested line *below* the cash line, HUBL 38%. Two lines that swap places
// need to be tellable apart where they touch, which weight and opacity alone do
// not manage at this width.
//
// It is also why the readout is a tooltip that names all three at the moment
// under the cursor, rather than a row of final values: at any given date, which
// of the two is ahead is a real question with a changing answer, and reading it
// off the bottom of the panel while looking at the middle of the plot does not
// work. Identity lives in two places so it survives both states — a corner
// legend when idle, the tooltip's own labels while hovering.
const VB_W = 100;
const VB_H = 40;
const PAD = 2;
// Horizontal inset, in viewBox units. Without it the last sample sits exactly on
// x=100 and the 2px stroke is half-clipped by the panel edge.
const PAD_X = 3;

interface Line {
    basis: Basis;
    label: string;
    points: { t: number; value: number }[];
    /** Screen pixels — the strokes are non-scaling. */
    width: number;
    dash?: string;
    opacity: number;
}

// Heaviest last so the most important line draws on top of the other two.
// Dashes are in screen pixels — the strokes are non-scaling.
const LINES: { basis: Basis; label: string; width: number; dash?: string; opacity: number }[] = [
    { basis: 'nav', label: 'Price only', width: 1, dash: '1 2.5', opacity: 0.5 },
    { basis: 'cash', label: 'Taken as cash', width: 1.5, dash: '5 3', opacity: 0.75 },
    { basis: 'drip', label: 'Reinvested', width: 2, opacity: 1 },
];

/** The line, drawn small, so a label is never identified by words alone. */
const Mark: Component<{ line: { width: number; dash?: string; opacity: number } }> = (props) => (
    <svg width='14' height='6' viewBox='0 0 14 6' class='shrink-0 text-foreground' aria-hidden='true'>
        <line x1='0' y1='3' x2='14' y2='3' stroke='currentColor' stroke-width={props.line.width} stroke-dasharray={props.line.dash} stroke-opacity={props.line.opacity} />
    </svg>
);

export const BasisChart: Component<{ symbol: string; height?: string }> = (props) => {
    const from = createMemo(() => {
        const first = candles(props.symbol)[0]?.date;
        return first == null ? null : Date.parse(first);
    });

    const lines = createMemo<Line[]>(() => {
        const start = from();
        if (start == null) return [];
        return LINES.map((spec) => ({ ...spec, points: valueSeries(props.symbol, spec.basis, start) })).filter((l) => l.points.length > 0);
    });

    const span = createMemo(() => {
        const all = lines().flatMap((l) => l.points);
        if (all.length === 0) return null;
        const values = all.map((p) => p.value);
        const times = all.map((p) => p.t);
        const lo = Math.min(...values);
        const hi = Math.max(...values);
        const t0 = Math.min(...times);
        const t1 = Math.max(...times);
        return { t0, tSpan: t1 - t0 || 1, lo, vSpan: hi - lo || Math.abs(hi) || 1 };
    });

    const x = (t: number): number => PAD_X + ((t - span()!.t0) / span()!.tSpan) * (VB_W - PAD_X * 2);
    const y = (v: number): number => VB_H - PAD - ((v - span()!.lo) / span()!.vSpan) * (VB_H - PAD * 2);

    const path = (l: Line): string => l.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(3)} ${y(p.value).toFixed(3)}`).join(' ');

    // The crosshair follows the shared date; the tooltip follows the pointer
    // being over *this* chart. Linked lines, one readout.
    const [over, setOver] = createSignal(false);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });
    const hoverT = (): number | null => {
        const t = hoverAt();
        if (t == null || span() == null) return null;
        // Outside this chart's window there is no line to mark — a fund that
        // listed in 2024 has nothing to say about a 2015 payment.
        return t < span()!.t0 || t > span()!.t0 + span()!.tSpan ? null : t;
    };

    /** Heaviest first, so both the legend and the tooltip read top line down —
        the order the lines sit in on the plot more often than not. */
    const ranked = createMemo(() => [...lines()].reverse());

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width <= 0 || !span()) return;
        // Inverts x(). Taking the fraction over the whole box ignores the PAD_X
        // inset and slides the crosshair off the cursor — see close-chart.
        const across = ((e.clientX - rect.left) / rect.width) * VB_W;
        const fraction = Math.min(1, Math.max(0, (across - PAD_X) / (VB_W - PAD_X * 2)));
        setHoverAt(span()!.t0 + fraction * span()!.tSpan);
        setCursor({ x: e.clientX, y: e.clientY });
        setOver(true);
    };

    /** Each line read at the same moment, which is the only way the three
        compare — and the whole point of reading them together. */
    const at = (l: Line): number | null => {
        const t = hoverT();
        const points = l.points;
        if (points.length === 0) return null;
        if (t == null) return points[points.length - 1].value;
        let found = points[0];
        for (const p of points) {
            if (p.t > t) break;
            found = p;
        }
        return found.value;
    };

    const when = () => {
        const t = hoverT();
        const points = lines()[0]?.points ?? [];
        if (points.length === 0) return null;
        if (t == null) return points[points.length - 1].t;
        let found = points[0].t;
        for (const p of points) {
            if (p.t > t) break;
            found = p.t;
        }
        return found;
    };

    return (
        <Show when={span()} fallback={<Text tone='muted' class='block px-2 py-6 text-center'>No price history</Text>}>
            <div class={`relative w-full ${props.height ?? 'h-56'}`} onMouseMove={track} onMouseLeave={() => { setOver(false); clearHover(); }}>
                <svg class='h-full w-full' viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio='none' fill='none'>
                    <For each={lines()}>
                        {(l) => (
                            <path
                                d={path(l)}
                                class='stroke-foreground'
                                stroke-width={l.width}
                                stroke-dasharray={l.dash}
                                stroke-opacity={l.opacity}
                                stroke-linejoin='round'
                                stroke-linecap={l.dash ? 'butt' : 'round'}
                                vector-effect='non-scaling-stroke'
                            />
                        )}
                    </For>
                    <Show when={hoverT() != null}>
                        <line x1={x(hoverT()!)} y1='0' x2={x(hoverT()!)} y2={VB_H} class='stroke-muted-foreground' stroke-width='1' vector-effect='non-scaling-stroke' />
                    </Show>
                </svg>

                {/* The legend, as a corner annotation rather than a row under the
                    chart. Three lines still need naming — identity by texture
                    alone is not identity — but that does not have to cost a whole
                    row of the panel.

                    Hidden while hovering, which is safe precisely because the
                    tooltip names every basis too: whichever state you are in,
                    something on screen says which line is which. Top-left is the
                    quiet corner here — all three series start together at the
                    left and climb, so the space above the start is empty on every
                    fund that has paid anything at all. */}
                <Show when={!over()}>
                    <div class='pointer-events-none absolute top-0 left-0 flex items-center gap-3 px-2 py-1 whitespace-nowrap'>
                        <For each={ranked()}>
                            {(l) => (
                                <span class='flex items-center gap-1'>
                                    <Mark line={l} />
                                    <Text tone='muted'>{l.label}</Text>
                                </span>
                            )}
                        </For>
                    </div>
                </Show>
            </div>

            {/* All three at one moment, which is the only way they compare — and
                the reason this is a tooltip rather than three separate ones. It
                carries the labels as well as the values, so it doubles as the
                legend while the corner annotation is hidden.

                Portalled and fixed: Panel is overflow-hidden and would clip a
                tooltip drawn inside the plot. */}
            <Show when={over() && hoverT() != null}>
                <Portal>
                    <div
                        class='pointer-events-none fixed z-[200] text-foreground rounded border bg-background px-2 py-1 shadow'
                        style={{ left: `${Math.min(cursor().x + 12, window.innerWidth - 240)}px`, top: `${Math.min(cursor().y + 12, window.innerHeight - 108)}px` }}>
                        <Text tone='muted' class='block whitespace-nowrap'>
                            {when() == null ? '' : formatDay(new Date(when()!).toISOString().slice(0, 10))}
                        </Text>
                        <For each={ranked()}>
                            {(l) => (
                                <div class='flex items-center justify-between gap-3 whitespace-nowrap'>
                                    <span class='flex items-center gap-1'>
                                        <Mark line={l} />
                                        <Text tone='muted'>{l.label}</Text>
                                    </span>
                                    <Text weight={l.basis === 'drip' ? 'medium' : undefined}>{formatCurrency(at(l))}</Text>
                                </div>
                            )}
                        </For>
                    </div>
                </Portal>
            </Show>

        </Show>
    );
};

export default BasisChart;
